import { atom, getDefaultStore } from 'jotai';
import { EPopup, PopupsState } from '@src/types/common';
import { AddToCollection } from '@src/types/Popups';
import { MediaView, CRMTemplateView } from 'common';
import { IPRightsTemplate } from '@src/types/IPRights';
import { ANALYTICS_ACTIONS, ANALYTICS_CATEGORIES, trackAnalyticsEvent } from '@src/utils/analyticsTracking';

const jotaiStore = getDefaultStore();

const initialPopups: PopupsState = {
  [EPopup.notfound]: false,
  [EPopup.account]: false,
  [EPopup.settings]: false,
  [EPopup.userSettings]: false,
  [EPopup.widgetCreation]: false,
  [EPopup.rights]: false,
  [EPopup.collectOverlay]: false,
  [EPopup.setCreatorCountriesOverlay]: false,
  [EPopup.getRights]: null,
  [EPopup.promptconfirm]: null,
  [EPopup.downloadCollection]: null,
  [EPopup.addInfluencer]: null,
  [EPopup.editInfluencer]: null,
  [EPopup.addBlacklist]: null,
  [EPopup.crmSettings]: false,
  [EPopup.crmAddTemplate]: false,
  [EPopup.templateSelector]: null,
};

export const popupsAtom = atom<PopupsState>(initialPopups);
export const popupLoadingAtom = atom<boolean>(false);

export const collectOverlayAtom = atom<{
  step: 'add' | 'new';
  media?: AddToCollection;
  isAutoCollection?: boolean;
}>({ step: 'add', media: undefined });

export const setCreatorCountriesAtom = atom<{ mediaList?: MediaView[] }>({ mediaList: undefined });

export const crmSelectedTemplateAtom = atom<CRMTemplateView | null>(null);

// Imperative helpers — usable outside React components via jotaiStore

export function setPopup<T extends EPopup>(popup: T, val: PopupsState[T]): void {
  jotaiStore.set(popupsAtom, (prev) => ({ ...prev, [popup]: val }));
}

export function openCollectOverlay(step: 'add' | 'new', media?: AddToCollection, isAutoCollection?: boolean): void {
  jotaiStore.set(collectOverlayAtom, (prev) => ({
    ...prev,
    step,
    media: media ?? prev.media,
    isAutoCollection,
  }));
  jotaiStore.set(popupsAtom, (prev) => ({ ...prev, [EPopup.collectOverlay]: true }));
}

export function openSetCreatorCountriesOverlay(mediaList: MediaView[]): void {
  jotaiStore.set(setCreatorCountriesAtom, { mediaList });
  jotaiStore.set(popupsAtom, (prev) => ({ ...prev, [EPopup.setCreatorCountriesOverlay]: true }));
}

export function setPopupLoading(loading: boolean): void {
  jotaiStore.set(popupLoadingAtom, loading);
}

export function setCrmSelectedTemplate(template: CRMTemplateView | null): void {
  jotaiStore.set(crmSelectedTemplateAtom, template);
}

// Rights requests atoms
export const rightsTemplateFrAtom = atom<IPRightsTemplate['fr']>(false);
export const rightsSelectedTemplateAtom = atom<CRMTemplateView | null>(null);

export function setRightsTemplateFr(fr: IPRightsTemplate['fr']): void {
  trackAnalyticsEvent(
    ANALYTICS_ACTIONS.rightsTemplateLanguageChanged,
    ANALYTICS_CATEGORIES.rightsRequests,
  );
  jotaiStore.set(rightsTemplateFrAtom, fr);
}

export function setRightsSelectedTemplate(template: CRMTemplateView | null): void {
  trackAnalyticsEvent(
    ANALYTICS_ACTIONS.rightsTemplateSelected,
    ANALYTICS_CATEGORIES.rightsRequests,
  );
  jotaiStore.set(rightsSelectedTemplateAtom, template);
}
